import { gql } from '@apollo/client'

/**
 * Fetches a user's subscription details for a single receipt-based provider
 * (Apple/iTunes, Google Play, Amazon, Roku) from the authentication subgraph.
 * Returns `providerSubscription: null` when the user has no receipt for that
 * provider — that is a valid "no subscription found" result, not an error.
 */
export const GET_PROVIDER_SUBSCRIPTION = gql`
  query ProviderSubscription($uid: ID!, $provider: String!) {
    providerSubscription(uid: $uid, provider: $provider) {
      provider
      status
      billingAccountId
      subscriptionId
      subscriptionNumber
      paidThroughDate
      startDate
      endDate
      nextReviewDate
      nextBillAmount
      currency
      cancelDate
      planSku
      planName
      userInfo {
        uid
        username
        email
        status
      }
    }
  }
`

/**
 * Per-provider billing-event counts for a user. The `/user-billing` page only needs to know
 * *which* providers to render a tab for, so this aliases `billingEventCount` once per provider
 * instead of pulling `billingEventList`.
 *
 * The `source` groupings mirror `SOURCE_TO_PROVIDER` in `group-billing-events.js` — card /
 * PayPal / Adyen payments run through Zuora, so they count toward the Zuora tab. The alias
 * keys match the `PROVIDER_*` constants (`itunes`, not `apple`).
 *
 * For the 2nd-party (receipt-based) providers — itunes / google / amazon / roku — the count is
 * scoped with `hasReceipt: true`, so it only counts events that actually join a receipt row
 * (`apple_receipts` / `google_receipts` / `amazon_receipts` / `roku_signup`). A provider whose
 * events have no matching receipt therefore reports 0 and gets no tab. Zuora has no receipt
 * table, so its count is unscoped.
 */
export const GET_USER_BILLING_EVENT_COUNTS = gql`
  query UserBillingEventCounts($uid: ID!) {
    zuora: billingEventCount(
      where: { uid: { eq: $uid }, source: { in: ["zuora", "paypal", "myo_paypal", "adyen"] } }
    ) {
      count
    }
    itunes: billingEventCount(
      where: { uid: { eq: $uid }, source: { in: ["apple", "itunes"] }, hasReceipt: true }
    ) {
      count
    }
    google: billingEventCount(
      where: { uid: { eq: $uid }, source: { eq: "google" }, hasReceipt: true }
    ) {
      count
    }
    amazon: billingEventCount(
      where: { uid: { eq: $uid }, source: { eq: "amazon" }, hasReceipt: true }
    ) {
      count
    }
    roku: billingEventCount(
      where: { uid: { eq: $uid }, source: { eq: "roku" }, hasReceipt: true }
    ) {
      count
    }
  }
`
